import { Header } from "app/components/sharingAssetModal/components/Header";
import { SharingStateContainer } from "app/components/sharingAssetModal/components/SharingStateContainer";
import { Footer } from "app/components/sharingAssetModal/components/Footer";
import type { IProModal } from "pro-features/components/Modals/src/ProModal";
import { ProModal } from "pro-features/components/Modals/src/ProModal";
import type { FC, ReactNode } from "react";
import styled from "styled-components";
import type { IModalText, ISharing } from "app/components/sharingAssetModal/types";
import { ESharingState } from "app/components/sharingAssetModal/types";
import { translate } from "app/filters/i18nFilter";
import { Banner } from "@similarweb/ui-components";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { Textfield } from "@similarweb/ui-components";
import { NotificationManager } from "app/components/sharingAssetModal/components/sharingStateRadioOptions/NotificationManager";
import { noop } from "lodash";

const { carbon } = colorsPalettes;

const ContentContainer = styled.div`
    width: 550px;
`;

const Title = styled.span`
    ${setFontVariant("body1", carbon[400])}
`;

const BannerContainer = styled.div`
    padding: 12px 24px;
    svg {
        path {
            fill: ${carbon[300]};
        }
    }
`;

const Icon = styled(SWReactIcons)`
    flex-shrink: 0;
`;

export const TextFieldTitle = styled.div`
    ${setFontVariant("body1", carbon[400])};
    line-height: 20px;
    margin-top: 16px;
    padding: 0 24px;
`;

export const TextFieldStyled = styled(Textfield)`
    padding: 16px 24px;
`;

const NotificationManagerContainer = styled.div`
    padding: 8px 24px;
`;

interface IMainModal {
    modalProps: IProModal;
    onShare: VoidFunction;
    onBannerClick: (sharing: ISharing) => void;
    text: IModalText;
    onCancel: VoidFunction;
    isLoading: boolean;
    sharing: ISharing;
    setSharing: (sharing: ISharing) => void;
    disabledSharingStates: Array<ESharingState>;
    hideSharingStates: Array<ESharingState>;
    CustomNameComponent?: ReactNode;
    showNotificationCheckboxInMainModal?: boolean;
    hideCancelButton?: boolean;
    checkboxText?: string;
}

export const MainModal: FC<IMainModal> = (props) => {
    const {
        modalProps,
        text,
        onShare,
        onCancel,
        onBannerClick = noop,
        isLoading,
        sharing,
        setSharing,
        disabledSharingStates,
        hideSharingStates,
        CustomNameComponent,
        showNotificationCheckboxInMainModal,
        hideCancelButton,
        checkboxText,
    } = props;
    const { header, sharingStateContainer, disabledStatesBannerKey, disabledStatesBannerButtonText } = text;
    const setSharingHighOrderFunction = (attribute: keyof ISharing) => (value) => {
        setSharing({ ...sharing, [attribute]: value });
    };
    const setNotification = setSharingHighOrderFunction("notification");
    const { notification } = sharing;
    return (
        <ProModal {...modalProps}>
            <ContentContainer>
                <Header {...header} />
                {CustomNameComponent}
                <SharingStateContainer
                    text={sharingStateContainer}
                    sharing={sharing}
                    setSharing={setSharing}
                    disabledSharingStates={disabledSharingStates}
                    hideSharingStates={hideSharingStates}
                    showNotificationCheckboxInMainModal={showNotificationCheckboxInMainModal}
                />
                {disabledSharingStates.length > 0 ? (
                    <BannerContainer>
                        {disabledStatesBannerButtonText ? (
                            <Banner
                                title={<Title>{translate(disabledStatesBannerKey)}</Title>}
                                CustomIcon={
                                    <Icon
                                        iconName="sparkles"
                                        size="sm"
                                    />
                                }
                                buttonType="trial"
                                buttonIconName="sparkles"
                                onButtonClick={() => onBannerClick(sharing)}
                                buttonText={disabledStatesBannerButtonText}
                            />
                        ) : (
                            <Banner
                                title={<Title>{translate(disabledStatesBannerKey)}</Title>}
                                CustomIcon={
                                    <Icon
                                        iconName="locked"
                                        size="sm"
                                    />
                                }
                            />
                        )}
                    </BannerContainer>
                ) : null}
                {showNotificationCheckboxInMainModal && sharing.sharingState !== ESharingState.PRIVATE ? (
                    <NotificationManagerContainer>
                        <NotificationManager
                            notification={notification}
                            setNotification={setNotification}
                            checkboxText={checkboxText}
                        />
                    </NotificationManagerContainer>
                ) : null}
                <Footer
                    text={text?.footer?.shareButton}
                    onShare={onShare}
                    onCancel={onCancel}
                    isLoading={isLoading}
                    sharing={sharing}
                    hideCancelButton={hideCancelButton}
                />
            </ContentContainer>
        </ProModal>
    );
};
